import React, { useEffect, useMemo, useState } from 'react';
import { QueryClientProvider, QueryClient } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Router, Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import idLocale from 'antd/es/locale/id_ID';
import enLocale from 'antd/es/locale/en_US';
import { RootState, selectCurrentCountry, selectCurrentLanguage, setUserCredential } from 'modules';
import { envCookieName, getCookie, LanguageCode } from 'common';
import { Toaster } from './components';
import { WatchlistProvider } from './components/Watchlist';
import { ColorPrimaryContext } from './contexts/ColorPrimaryContext';
import SkinStyles from './components/SkinStyles';
import { history } from './store';
import { ProtectedRoute, PublicRoute } from './routes/index';
import './AppV2.less';

import 'common/util/dayjsConfig';

const Page404 = React.lazy(() => import('./views/Errors/Page404'));
const Page500 = React.lazy(() => import('./views/Errors/Page500'));
const SupportReport = React.lazy(() => import('./views/SupportReport'));
const ForgetPassword = React.lazy(() => import('./views/ForgetPassword'));
const Login = React.lazy(() => import('./views/Login'));
const TwoFA = React.lazy(() => import('./views/2FA'));
const TheLayout = React.lazy(() => import('./containers/TheLayout'));
const SwitchEmployerCompany = React.lazy(() => import('./views/SwitchEmployerCompany'));
const SetupPassword = React.lazy(() => import('./views/SetupPassword'));

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

export const handleLogout = () => {
  localStorage.removeItem('persist:user');
  window.location.reload();
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // automatically requests fresh data for you in the background
      refetchOnWindowFocus: false,
      retry: 1,
      // will be return after 30 seconds
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Containers

// eslint-disable-next-line react/prefer-stateless-function
const App: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((userState: RootState) => userState.user);

  const countryCode = useSelector((state: RootState) => selectCurrentCountry(state));
  const langaugeCode = useSelector((stateData: RootState) => selectCurrentLanguage(stateData));

  const [colorPrimary, setColorPrimary] = useState(SkinStyles.Default.colorPrimary);
  const colorPrimaryContextValue = useMemo(() => ({ colorPrimary, setColorPrimary }), [colorPrimary]);

  // const [reloadState, setReloadState] = useState(false);
  useEffect(() => {
    queryClient.clear();
  }, [countryCode]);

  useEffect(() => {
    const permissionsCookie = JSON.parse(
      getCookie('permissions') === '' || getCookie('permissions') === undefined ? '[]' : getCookie('permissions')
    );
    const employersCookie = JSON.parse(
      getCookie('employerProfiles') === '' || getCookie('employerProfiles') === undefined
        ? '[]'
        : getCookie('employerProfiles')
    );
    const cUser = { ...user };
    const employerIdCookie = getCookie(envCookieName('employerId')) ?? '';
    const eidMismatch = (user.eid ?? '') !== employerIdCookie;
    const permissionsMismatch = !user.permissions.every((p: string) => permissionsCookie.includes(p));
    if (permissionsMismatch || eidMismatch) {
      let accessToken = getCookie('accessToken');
      if (!accessToken) {
        const chunkCount = parseInt(getCookie('accessTokenChunks') || '0', 10);
        if (chunkCount > 0) {
          accessToken = '';
          for (let i = 0; i < chunkCount; i += 1) {
            accessToken += getCookie(`accessTokenChunk${i}`) || '';
          }
        }
      }
      cUser.accessToken = accessToken;
      cUser.refreshToken = getCookie(envCookieName('refreshToken'));
      cUser.expiredOn = Number(getCookie(envCookieName('expiredOn')));
      cUser.eid = getCookie(envCookieName('employerId')) ?? undefined;
      cUser.employers = employersCookie;
      cUser.permissions = permissionsCookie;
      dispatch(setUserCredential(cUser));
    }
  });

  // useEffect(() => {
  //   if (reloadState) {
  //     setReloadState(() => false);
  //     sessionStorage.setItem('reload', 'true');
  //     window.location.reload();
  //   }
  // }, [reloadState]);

  // useEffect(() => {
  //   const handleStorageEvent = (event: StorageEvent): void => {
  //     if (event.key === 'requestSyncSessionStorage') {
  //       localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
  //       localStorage.removeItem('sessionStorage');
  //     } else if (event.key === 'sessionStorage') {
  //       const sessionStorageItem = JSON.parse(event.newValue || '{}');
  //       // eslint-disable-next-line no-restricted-syntax, guard-for-in
  //       for (const key in sessionStorageItem) {
  //         sessionStorage.setItem(key, sessionStorageItem[key]);
  //       }
  //       sessionStorage.setItem('reload', 'false');
  //       setReloadState(() => true);
  //     }
  //   };

  //   const requestSyncSessionStorage = (): void => {
  //     if (!sessionStorage.length) {
  //       localStorage.setItem('requestSyncSessionStorage', dayjs().unix().toString());
  //     }
  //   };

  //   window.addEventListener('storage', handleStorageEvent);
  //   requestSyncSessionStorage();

  //   return () => {
  //     window.addEventListener('storage', handleStorageEvent);
  //     requestSyncSessionStorage();
  //   };
  // }, []);

  const locale = useMemo(() => {
    if (langaugeCode === LanguageCode.ID) {
      return idLocale;
    }
    return enLocale;
  }, [langaugeCode]);

  return (
    <ColorPrimaryContext.Provider value={colorPrimaryContextValue}>
      <ConfigProvider
        locale={locale}
        theme={{
          token: {
            colorPrimary,
            colorLink: colorPrimary,
          },
          hashed: false,
        }}
        prefixCls="antd-v5"
      >
        <QueryClientProvider client={queryClient}>
          <WatchlistProvider>
            <Router history={history}>
              <React.Suspense fallback={loading}>
                <Toaster />
                <Switch>
                  <Route exact path="/404" component={Page404} />
                  <Route exact path="/500" component={Page500} />
                  <PublicRoute exact path="/support-report/:data" component={SupportReport} />
                  <PublicRoute exact path="/forget-password" component={ForgetPassword} />
                  <PublicRoute exact path="/login" component={Login} />
                  <PublicRoute exact path="/2fa" component={TwoFA} />
                  <ProtectedRoute exact path="/setup-password" component={SetupPassword} />
                  <ProtectedRoute exact path="/employer-company" component={SwitchEmployerCompany} />
                  <ProtectedRoute path="/" component={TheLayout} />
                </Switch>
              </React.Suspense>
            </Router>
            <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />
          </WatchlistProvider>
        </QueryClientProvider>
      </ConfigProvider>
    </ColorPrimaryContext.Provider>
  );
};

export default App;
